import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { CardSkeleton } from '@/components/ui/Skeleton';
import { formatCurrency, cn } from '@/lib/utils';
import type { BudgetPacing } from '@/lib/insightsApi';

interface Props {
  data?: BudgetPacing;
  isLoading?: boolean;
  currency?: string;
}

const STATUS_STYLE: Record<string, string> = {
  on_track: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  over_pacing: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  under_pacing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

export function BudgetPacingWidget({ data, isLoading, currency = 'PLN' }: Props) {
  const { t } = useTranslation();

  if (isLoading) return <CardSkeleton />;
  if (!data) return null;

  if (data.status === 'no_budget_set' || data.status === 'invalid_dates' || data.monthly_budget === undefined) {
    return (
      <div className="card p-5">
        <h3 className="text-sm font-semibold dark:text-white mb-2">
          {t('insights.budget_pacing.title')}
        </h3>
        <p className="text-xs text-slate-500">
          {t('insights.budget_pacing.no_budget', { defaultValue: 'Brak ustawionego miesięcznego budżetu dla tego konta.' })}
        </p>
      </div>
    );
  }

  const monthlyBudget = data.monthly_budget ?? 0;
  const spentSoFar = data.spent_so_far ?? 0;
  const expectedSpend = data.expected_spend ?? 0;
  const variance = data.variance ?? 0;
  const variancePct = data.variance_pct ?? 0;

  const spentPct = monthlyBudget > 0
    ? Math.min((spentSoFar / monthlyBudget) * 100, 100)
    : 0;

  const expectedPct = monthlyBudget > 0
    ? Math.min((expectedSpend / monthlyBudget) * 100, 100)
    : 0;

  const statusKey = data.status?.toLowerCase().replace(/ /g, '_') ?? 'on_track';
  const statusStyle = STATUS_STYLE[statusKey] ?? STATUS_STYLE.on_track;

  const variancePositive = variancePct > 0;
  const varianceNeutral = variancePct === 0;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold dark:text-white">
          {t('insights.budget_pacing.title')}
        </h3>
        <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full', statusStyle)}>
          {t(`insights.budget_pacing.status.${statusKey}`, { defaultValue: data.status })}
        </span>
      </div>

      {/* Progress bars */}
      <div className="space-y-3 mb-4">
        {/* Actual spend */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-600 dark:text-slate-400">
              {t('insights.budget_pacing.spent')}
            </span>
            <span className="font-medium dark:text-white">
              {formatCurrency(spentSoFar, currency)} / {formatCurrency(monthlyBudget, currency)}
            </span>
          </div>
          <div className="h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-500 transition-all"
              style={{ width: `${spentPct}%` }}
            />
          </div>
        </div>

        {/* Expected spend */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-500 dark:text-slate-400">
              {t('insights.budget_pacing.expected')}
            </span>
            <span className="text-slate-500">
              {formatCurrency(expectedSpend, currency)}
            </span>
          </div>
          <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-slate-300 dark:bg-slate-600 transition-all"
              style={{ width: `${expectedPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Variance */}
      <div className={cn(
        'flex items-center gap-2 text-sm font-medium mb-3',
        varianceNeutral ? 'text-slate-400' : variancePositive ? 'text-red-500' : 'text-emerald-600',
      )}>
        {varianceNeutral
          ? <Minus size={14} />
          : variancePositive
          ? <TrendingUp size={14} />
          : <TrendingDown size={14} />}
        <span>
          {variancePositive ? '+' : ''}{variancePct.toFixed(1)}%{' '}
          ({formatCurrency(Math.abs(variance), currency)})
        </span>
      </div>

      {data.recommendation && (
        <p className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 rounded-lg p-2.5">
          {data.recommendation}
        </p>
      )}
    </div>
  );
}
